import { Injectable, type Signal, computed, type OutputEmitterRef, inject, type WritableSignal } from "@angular/core";
import { WkTranslateService } from "@shared/utils";
import type {
    WkDataTableContext,
    WkDataTableInternalData,
    WkDataTableSorting,
    WkDataTableActionsFunction,
    WkDataTableInternalDataItem,
    WkDataTableActions,
} from "@components/data-table/data-table.types";

@Injectable()
export class WkDataTableContextService<TItem> {
    /**
     * Service for managing the context of a data table, including configuration, sorting, searching, and actions.
     */
    private readonly wkTranslate = inject(WkTranslateService);

    /**
     * Internal signals for managing the data table context.
     */
    private _enableRowClick?: Signal<boolean>;
    private _onRowClick?: OutputEmitterRef<WkDataTableInternalDataItem<TItem>>;
    private _selectable?: Signal<boolean>;
    private _sorting?: Signal<WkDataTableSorting>;
    private _primaryField?: Signal<keyof TItem & string>;
    private _actions?: Signal<WkDataTableActions<TItem>>;
    private _reorderableColumns?: Signal<boolean>;

    /**
     * All context public properties exposed by the context service.
     */
    public readonly enableRowClick = computed(() => this._enableRowClick?.() ?? false);
    public onRowClick(): OutputEmitterRef<WkDataTableInternalDataItem<TItem>> | undefined {
        return this._onRowClick;
    }
    public readonly selectable = computed(() => this._selectable?.() ?? false);
    public readonly sorting = computed(() => this._sorting?.() ?? undefined);
    public readonly primaryField = computed(() => this._primaryField?.() ?? undefined);
    public readonly reorderableColumns = computed(() => this._reorderableColumns?.() ?? false);

    /**
     * Data
     */
    public data?: WritableSignal<WkDataTableInternalData<TItem>>;

    /**
     * Actions
     */
    public actions = computed<WkDataTableActionsFunction<TItem>>(() => {
        const actions = this._actions?.();
        return (item?: WkDataTableInternalDataItem<TItem>) => {
            if (!actions || !actions.length) return [];
            return actions.map((action) => {
                const title = typeof action.title === "function" ? action.title(item) : action.title;
                const icon = typeof action.icon === "function" ? action.icon(item) : action.icon;
                return {
                    ...action,
                    title: this.wkTranslate.translate(title),
                    icon,
                };
            });
        };
    });

    /**
     * Sets the context for the data table, including configuration such as row click behavior, selectable state, sorting, primary field, and actions.
     * @param context The context object containing configuration for the data table.
     */
    public setContext(context: WkDataTableContext<TItem>) {
        this._enableRowClick = context.enableRowClick;
        this._onRowClick = context.onRowClick;
        this._selectable = context.selectable;
        this.data = context.data;
        this._sorting = context.sorting;
        this._primaryField = context.primaryField;
        this._actions = context.actions;
        this._reorderableColumns = context.reorderableColumns;
    }
}
